import { useState, useEffect } from "react";
import PageShell from "@/components/layout/PageShell";
import Seo from "@/components/seo/Seo";
import { breadcrumbSchema } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";
import { ArrowRight, Mail } from "lucide-react";
import SampleReportDialog from "@/components/booking/SampleReportDialog";

const PREVIEW_IMG = "/images/sample-report-cover-800.png";

// Verified figures from the approved sample PDF (102 pages): 25 Low / 52 Medium
// / 11 High = 88 findings. This sample only — never company-wide statistics.
const SUMMARY = [
  { label: "Low", count: 25, chip: "bg-[#E7F6EE] text-[#177A4C] border-[#BBE5CE]" },
  { label: "Medium", count: 52, chip: "bg-[#FFF3E6] text-[#B54708] border-[#FFDCB8]" },
  { label: "High", count: 11, chip: "bg-[#FDECEC] text-[#C81E2B] border-[#F8C6C9]" },
];

export default function SampleReport() {
  const path = "/sample-snagging-report";
  const [dialogOpen, setDialogOpen] = useState(false);
  useEffect(() => { trackEvent("sample_report_view"); }, []);

  const openDialog = (location) => {
    trackEvent("sample_modal_opened", { location });
    setDialogOpen(true);
  };

  return (
    <PageShell breadcrumbs={[{ name: "Sample report", path }]}>
      {({ onQuote }) => (
        <>
          <Seo
            title="Sample Property Inspection Report Dubai | Fedari"
            description="View a sample Fedari property inspection report with photographic findings organised by location and rated Low, Medium or High. Get an inspection quote."
            path={path}
            jsonLd={[breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Sample report", path }])]}
          />
          <section className="wrap py-8 lg:py-10">
            <span className="eyebrow">Your deliverable</span>
            <h1 className="text-[clamp(2rem,4vw,2.75rem)] tracking-[-0.03em] mt-2 mb-3 max-w-[22ch]">See What a Fedari Inspection Report Includes</h1>
            <p className="text-[1.15rem] text-bodytext max-w-[62ch] mb-6">
              Explore a sample Fedari inspection report, showing photographic findings organised by location and prioritised as Low, Medium or High. See how visible observations are documented to support clear follow-up with the responsible party.
            </p>

            <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-6">
              <button onClick={() => openDialog("hero_cta")} data-testid="sample-view-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                <Mail size={16} /> Email Me the Sample Report
              </button>
              <button onClick={onQuote} data-testid="sample-quote-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold bg-white hover:border-ink transition-colors">
                Get a Quote and Check Availability <ArrowRight size={16} />
              </button>
            </div>

            {/* Verified sample summary — labels + numbers, not colour alone */}
            <div className="max-w-[80ch] mb-2" data-testid="sample-summary">
              <div className="grid grid-cols-3 gap-3 max-w-[480px]">
                {SUMMARY.map((s) => (
                  <div key={s.label} className={`border rounded-xl px-4 py-3 ${s.chip}`}>
                    <b className="text-xl font-extrabold block leading-none">{s.count}</b>
                    <span className="text-[0.75rem] uppercase tracking-wide font-semibold">{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[0.8rem] text-muted max-w-[62ch] mb-6" data-testid="sample-caption">
              Illustrative sample: 88 findings from one inspection. The findings and their distribution vary by property, access and agreed inspection scope.
            </p>

            {/* Static preview image — never loads the PDF */}
            <div className="border border-line rounded-2xl overflow-hidden mb-8 max-w-[800px] bg-white" data-testid="sample-report-preview">
              <div className="px-4 py-2.5 border-b border-line bg-soft flex items-center justify-between gap-3">
                <span className="text-xs text-muted font-mono-jb tracking-wide uppercase">Sample report — preview</span>
              </div>
              <button onClick={() => openDialog("preview_image")} className="block w-full cursor-pointer" data-testid="sample-preview-image">
                <img src={PREVIEW_IMG} alt="Fedari sample property inspection report — inspector holding tablet showing report cover"
                  className="w-full h-auto" loading="lazy" width="800" height="450" />
              </button>
              <p className="px-4 py-2.5 border-t border-line bg-soft text-[0.8rem] text-muted">
                Preview image only. Enter your details to receive the complete sample report by email.
              </p>
            </div>

            {/* What the report actually contains — verified against the sample PDF */}
            <div className="max-w-[80ch] mb-8">
              <h2 className="text-lg mb-3">How the report is organised</h2>
              <ul className="space-y-2 text-bodytext text-sm">
                <li className="flex gap-2.5"><span className="text-blue font-bold flex-none">·</span> A cover summary with the total findings and their Low / Medium / High distribution.</li>
                <li className="flex gap-2.5"><span className="text-blue font-bold flex-none">·</span> One page per finding: photographs, a category, the location with plan coordinates, and a Low, Medium or High priority rating.</li>
                <li className="flex gap-2.5"><span className="text-blue font-bold flex-none">·</span> Each finding carries a status (for example Open) plus created and target dates — these are workflow aids for follow-up, not guaranteed developer repair deadlines or contractual service levels.</li>
                <li className="flex gap-2.5"><span className="text-blue font-bold flex-none">·</span> Every page of the sample carries a visible <b>Sample</b> watermark.</li>
              </ul>
              <p className="text-[0.8rem] text-muted mt-3">Findings shown in the sample are from one specific inspection — illustrative of the format, not evidence about any other property.</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button onClick={() => openDialog("bottom_cta")} data-testid="sample-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-blue text-white px-6 py-3.5 rounded-[10px] font-semibold hover:bg-blue-600 transition-colors">
                <Mail size={16} /> Email Me the Sample Report
              </button>
              <button onClick={onQuote}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-[10px] border border-line text-ink font-semibold hover:border-blue hover:text-blue transition-colors">
                Get a Quote and Check Availability <ArrowRight size={16} />
              </button>
            </div>
          </section>

          <SampleReportDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onQuote={onQuote} />
        </>
      )}
    </PageShell>
  );
}
